import { useState, useEffect } from 'react';
import { useTheme } from '@app/theme/ThemeContext';
import { useTimezone } from '@app/contexts/useTimezone';
import { ChartControls } from '@app/components/charts/ChartControls';
import { ForecastModel } from '@app/common/static-data/models';

import { useAlignedForecasts } from '../hooks/useAlignedForecasts';
import { useChartEventHandlers } from '../hooks/useChartEventHandlers';
import { useChartData } from '../hooks/useChartData';
import { useChartInstance } from '../hooks/useChartInstance';
import { PlottableForecast, VariableId, ModelId } from '../types';

interface ForecastEChartProps {
  data: Record<ModelId, PlottableForecast>;
  selectedParam: VariableId;
  selectedModels: ForecastModel[];
  endHour: number;
  customModelColors: Record<string, string>;
}

// Utility function to detect mobile devices
const isMobileDevice = (): boolean => {
  // Check if window is available (for SSR compatibility)
  if (typeof window === 'undefined') return false;

  // Check for touch capability and small screen width
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  // Additional mobile user agent detection
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent,
  );

  return (hasTouch && isSmallScreen) || isMobileUserAgent;
};

export function ForecastEChart({
  data = {} as Record<ModelId, PlottableForecast>,
  selectedParam,
  selectedModels,
  endHour,
  customModelColors,
}: ForecastEChartProps) {
  const { theme } = useTheme();
  const { timezone, formatInUserTimezone } = useTimezone();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#D1D5DB' : '#6B7280';

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMeanAndConfidence, setShowMeanAndConfidence] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isMobileDevice());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get aligned forecast data
  const alignedData = useAlignedForecasts(data, selectedModels, endHour);

  // Process chart data
  const chartData = useChartData({
    alignedData,
    selectedModels,
    selectedParam,
    showMeanAndConfidence,
    customModelColors,
  });

  // Manage chart instance
  const { chartRef, chartInstance } = useChartInstance({
    isDark,
    axisColor,
    chartData,
    timezone,
    formatInUserTimezone,
  });

  // Chart event handlers
  const { containerRef, resetZoom, toggleFullscreen, downloadChart } = useChartEventHandlers(
    chartInstance,
    selectedParam,
    isDark,
  );

  // Handle fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col w-full h-full min-h-[400px] ${isDark ? 'bg-gray-800' : 'bg-white'}`}
    >
      <div className="flex-shrink-0">
        <ChartControls
          isFullscreen={isFullscreen}
          onResetZoom={resetZoom}
          onToggleFullscreen={toggleFullscreen}
          onDownload={downloadChart}
          showMeanAndConfidence={showMeanAndConfidence}
          onToggleMeanConfidence={() => setShowMeanAndConfidence((v) => !v)}
          showFullscreen={!isMobile}
        />
      </div>
      <div ref={chartRef} className="w-full flex-1 min-h-[350px]" />
    </div>
  );
}
